import {
  Box,
  Button,
  Divider,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  // Select,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { useContext, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { createDialogContext, DialogProvider } from "../../../Common/Dialog";
import {
  AdminTicketType,
  KYDHold,
  KYDTicketTypeAccessCode,
  KYDTicketTypePool,
} from "@common/interfaces/KYDTicket";
import * as y from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import CurrencyInput from "react-currency-input-field";
import InputWithLabel from "../TicketTypes/TieredTicketType/InputWithLabel";
import DatepickerFormInput from "../TicketTypes/DatepickerFormInput";
import dayjs from "dayjs";

export const TicketHoldAddDialogContext = createDialogContext();

// Define the form data type
export type TicketHoldFormData = {
  ticket_type_id: string;
  quantity: number;
  price: string; // remains string as input returns string, can be empty
  label: string;
  expires_at: string; // can be empty string if not set
};

const TicketHoldSchema = y.object({
  ticket_type_id: y
    .object({
      value: y.string().required(),
      label: y.string().required(),
    })
    .required(),
  quantity: y.number().typeError("Quantity is required").required().min(1),
  price: y.string().required(),
  label: y.string().required(),
  expires_at: y.string().optional(),
});

type TicketHoldFormSchemaType = y.InferType<typeof TicketHoldSchema>;

interface TicketHoldAddDialogProps {
  ticketTypePools: KYDTicketTypePool[];
  ticketTypes: AdminTicketType[];
  timezone: string;
  isLoading?: boolean;
  onClose: () => void;
  onSave: (
    data: Omit<TicketHoldFormSchemaType, "ticket_type_id"> & {
      ticket_type_id: string;
    }
  ) => Promise<void>;
  mode?: "create" | "edit";
  hold?: KYDHold & { id?: string };
}

const TicketHoldAddDialog = ({
  ticketTypePools = [],
  ticketTypes = [],
  timezone,
  onSave,
  onClose,
  isLoading = false,
  mode = "create",
  hold,
}: TicketHoldAddDialogProps) => {
  const { isOpen } = useContext(TicketHoldAddDialogContext);

  const methods = useForm<TicketHoldFormSchemaType>({
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      ticket_type_id: hold?.ticket_type_id
        ? {
            value: hold.ticket_type_id,
            label:
              ticketTypes.find((tt) => tt.id === hold.ticket_type_id)?.name ||
              "Unknown Ticket Type",
          }
        : undefined,
      quantity: hold?.quantity || 1,
      price: hold?.price.units.toString(),
      label: hold?.name,
      expires_at: hold?.expires_at,
    },
    resolver: yupResolver(TicketHoldSchema, {
      abortEarly: false,
    }),
  });

  useEffect(() => {
    console.log({ errors: methods.formState.errors });
  }, [methods.formState.errors]);

  const _onSave = async (data: TicketHoldFormSchemaType) => {
    const dirty = methods.formState.dirtyFields;

    // Helper to check if a nested object (like ticket_type_id) is dirty
    const isFieldDirty = (field: keyof typeof dirty) => {
      const value = dirty[field];
      if (typeof value === "boolean") return value;
      // For nested objects, RHF marks the field as an object containing leaf booleans
      return (
        value && Object.values(value as Record<string, boolean>).some(Boolean)
      );
    };

    const payload: Partial<
      Omit<TicketHoldFormSchemaType, "ticket_type_id"> & {
        ticket_type_id: string;
      }
    > = {};

    if (mode === "create") {
      // In create mode, we need to send the full payload
      payload.ticket_type_id = data.ticket_type_id.value;
      payload.quantity = data.quantity;
      payload.price = data.price;
      payload.label = data.label;
      payload.expires_at = data.expires_at;
    } else {
      // Edit mode – send only dirty fields
      if (isFieldDirty("ticket_type_id")) {
        payload.ticket_type_id = data.ticket_type_id.value;
      }
      if (isFieldDirty("quantity")) {
        payload.quantity = data.quantity;
      }
      if (isFieldDirty("price")) {
        payload.price = data.price;
      }
      if (isFieldDirty("label")) {
        payload.label = data.label;
      }
      if (isFieldDirty("expires_at")) {
        payload.expires_at = data.expires_at;
      }
    }

    console.log({ payload });
    // Cast because we may be sending a subset in edit mode
    onSave(payload as any);
  };

  useEffect(() => {
    console.log({ ticketTypes });
  }, [ticketTypes]);

  // Reset form when hold or dialog open state changes
  useEffect(() => {
    if (isOpen) {
      methods.reset({
        ticket_type_id: hold?.ticket_type_id
          ? {
              value: hold.ticket_type_id,
              label:
                ticketTypes.find((tt) => tt.id === hold.ticket_type_id)?.name ||
                "Unknown Ticket Type",
            }
          : undefined,
        quantity: hold?.quantity || 1,
        price: hold?.price.units.toString(),
        label: hold?.name,
        expires_at: hold?.expires_at,
      });
    }
  }, [hold, isOpen, methods.reset, ticketTypes]);

  const _onClose = () => {
    methods.reset();
    onClose();
  };

  // Combine parent and internal loading state
  const loading = isLoading || methods.formState.isSubmitting;

  return (
    <Modal isOpen={isOpen} onClose={_onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {mode === "edit" ? "Edit Hold" : "Create Hold"}
        </ModalHeader>
        <ModalBody>
          <FormProvider {...methods}>
            <VStack spacing={2} align="stretch">
              <Box mb={6}>
                <Text fontWeight={"medium"}>Ticket Type</Text>

                <Controller
                  control={methods.control}
                  name="ticket_type_id"
                  render={({ field }) => (
                    <Select
                      options={ticketTypePools.map((pool) => ({
                        value: pool.ticket_types?.at(0),
                        label: pool.name,
                      }))}
                      defaultValue={field.value as any}
                      value={field.value}
                      isDisabled={isLoading || mode === "edit"}
                      onChange={(option) => {
                        field.onChange(option);
                      }}
                      getOptionValue={(option) => option.value}
                      components={{
                        Option: ({ innerRef, innerProps, data }) => (
                          <VStack
                            w="full"
                            p={2}
                            px={4}
                            borderBottom="1px dashed #E6E6E6"
                            ref={innerRef}
                            sx={{
                              "&:hover": {
                                backgroundColor: "gray.50",
                              },
                            }}
                            cursor="pointer"
                            {...innerProps}
                          >
                            {ticketTypes
                              .filter(
                                (tt) =>
                                  tt.pool?.ticket_types?.at(0) === data.value ||
                                  tt.id === data.value
                              )
                              .map((tt) => (
                                <HStack key={tt.id} fontSize="sm" w="full">
                                  <Text fontWeight="bold">{tt.name}</Text>
                                  <Text fontSize="sm" color="gray.500">
                                    ({tt.remaining} available)
                                  </Text>
                                </HStack>
                              ))}
                          </VStack>
                        ),
                      }}
                      formatOptionLabel={(option) => option.label}
                    />
                  )}
                />
                {methods.formState.errors.ticket_type_id && (
                  <Text color="red.300" fontSize="sm">
                    {methods.formState.errors.ticket_type_id.message}
                  </Text>
                )}
              </Box>
              <HStack>
                <Box flex={1}>
                  <InputWithLabel
                    label="Quantity"
                    placeholder="Quantity"
                    name="quantity"
                    inputProps={{
                      disabled: isLoading,
                    }}
                  />
                </Box>

                <Box flex={1}>
                  <InputWithLabel
                    name="price"
                    label={`Price`}
                    renderInput={({ field, errors }) => (
                      <>
                        <CurrencyInput
                          //@ts-ignore
                          borderColor={
                            methods.formState.errors.price ? "red" : "inherit"
                          }
                          borderWidth={methods.formState.errors.price ? 2 : 1}
                          style={{ width: "100%" }}
                          disabled={field.disabled || loading}
                          placeholder={`Price`}
                          defaultValue={field.value}
                          value={field.value}
                          onValueChange={(value) => {
                            methods.setValue(`price`, value || "", {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                          intlConfig={{
                            locale: "en-US",
                            currency: "USD",
                          }}
                          customInput={Input}
                          step={1}
                        />
                      </>
                    )}
                  />
                </Box>
              </HStack>
              <Box>
                <InputWithLabel
                  label="Name"
                  placeholder="e.g., Agent Hold, Label Partners"
                  name="label"
                  inputProps={{
                    disabled: isLoading,
                  }}
                />
              </Box>
              <Box>
                <DatepickerFormInput
                  label="Release Date (Optional)"
                  name="expires_at"
                  timezone={timezone}
                  showTimezone={true}
                  validate={(val) => {
                    return dayjs(val).isAfter(dayjs());
                  }}
                  validationFailMessage="Start date must be in the future"
                />
              </Box>
            </VStack>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <Stack direction={["column-reverse", "row-reverse"]} width="100%">
            <Button onClick={_onClose} isDisabled={loading} isLoading={loading}>
              Cancel
            </Button>
            <Button
              colorScheme={mode === "edit" ? "blue" : "green"}
              onClick={methods.handleSubmit(_onSave)}
              isLoading={loading}
              //isDisabled={!methods.formState.isValid || loading}
            >
              {mode === "edit" ? "Save Changes" : "Create Hold"}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withAddTicketHoldDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) =>
    (
      <DialogProvider context={TicketHoldAddDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );

export default TicketHoldAddDialog;
